import { useMemo, useState } from 'react'

import { ActiveFilters } from '@/components/judgments/ActiveFilters'
import { CompletenessBanner } from '@/components/judgments/CompletenessBanner'
import { ExportBar } from '@/components/judgments/ExportBar'
import { FilterPanel } from '@/components/judgments/FilterPanel'
import { JudgmentDetailDrawer } from '@/components/judgments/JudgmentDetailDrawer'
import { JudgmentTable } from '@/components/judgments/JudgmentTable'
import { Pagination } from '@/components/judgments/Pagination'
import { SearchBar } from '@/components/judgments/SearchBar'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { CountBadge } from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { useCaptures } from '@/hooks/useJobs'
import { useFacets, useJudgmentSearch } from '@/hooks/useJudgments'
import { useSearchState } from '@/hooks/useSearchState'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type { Judgment } from '@/types/api'

const FilterIcon = () => (
  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
    <path
      d="M2 4h12M4.5 8h7M6.5 12h3"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
)

const NoResultsIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden="true">
    <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M15.5 15.5L21 21"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
)

export function SearchPage() {
  const search = useSearchState()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)

  const query = useMemo(
    () => ({
      filters: search.filters,
      page: search.page,
      pageSize: search.pageSize,
      sortBy: search.sortBy,
      sortDir: search.sortDir,
    }),
    [search.filters, search.page, search.pageSize, search.sortBy, search.sortDir],
  )

  const results = useJudgmentSearch(query)
  const facets = useFacets(search.filters)
  const captures = useCaptures()

  const judgments = results.data?.items ?? []
  const meta = results.data?.meta
  const totalItems = meta?.totalItems ?? 0

  const cappedCaptures = useMemo(
    () => (captures.data ?? []).filter((capture) => capture.isCapped),
    [captures.data],
  )

  const onSelect = (judgment: Judgment) => setSelectedId(judgment.id)

  return (
    <>
      <PageHeader
        title="Search judgments"
        description="Everything stored from the New Jersey statewide Civil Judgment and Order Docket. Filters and sort live in the address bar, so a search is shareable."
      />

      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-2">
          <SearchBar
            value={search.filters.q}
            onChange={(value) => search.setFilters({ q: value })}
          />
          <Button
            size="lg"
            variant="secondary"
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            icon={<FilterIcon />}
            className="lg:hidden"
          >
            Filters
            {search.activeFilterCount > 0 ? (
              <CountBadge value={search.activeFilterCount} active />
            ) : null}
          </Button>
        </div>

        <ActiveFilters
          filters={search.filters}
          onChange={search.setFilters}
          onToggleArray={search.toggleArrayFilter}
          onReset={search.resetFilters}
        />
      </div>

      <CompletenessBanner
        cappedCaptures={cappedCaptures.map((capture) => capture.label)}
        totalCaptures={(captures.data ?? []).length}
      />

      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[16.5rem_minmax(0,1fr)] lg:gap-5">
        <aside
          className={cn('lg:block', filtersOpen ? 'block' : 'hidden')}
          aria-label="Filters"
        >
          <FilterPanel
            className={cn(
              'scrollbar-slim rounded-lg border border-line bg-surface-raised p-4 shadow-xs',
              'lg:sticky lg:top-18 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto',
            )}
            filters={search.filters}
            facets={facets.data}
            activeCount={search.activeFilterCount}
            onChange={search.setFilters}
            onToggleArray={search.toggleArrayFilter}
            onReset={search.resetFilters}
          />
        </aside>

        <Card className="min-w-0 overflow-hidden">
          <div className="flex items-center justify-between gap-3 border-b border-line bg-surface-raised px-4 py-2.5">
            <p className="text-sm text-ink-secondary" aria-live="polite">
              <span className="tabular font-semibold text-ink">
                {formatNumber(totalItems)}
              </span>{' '}
              judgment{totalItems === 1 ? '' : 's'}
              {results.isFetching ? (
                <span className="text-ink-muted"> · updating…</span>
              ) : null}
            </p>
            <ExportBar
              query={{
                filters: search.filters,
                sortBy: search.sortBy,
                sortDir: search.sortDir,
              }}
              totalItems={totalItems}
              disabled={results.isLoading}
            />
          </div>

          {results.isError ? (
            <ErrorState
              error={results.error}
              fallbackTitle="The search failed"
              onRetry={() => void results.refetch()}
            />
          ) : judgments.length === 0 && !results.isLoading ? (
            <EmptyState
              icon={<NoResultsIcon />}
              title={
                search.activeFilterCount > 0
                  ? 'No judgments match these filters'
                  : 'No judgments stored yet'
              }
              description={
                search.activeFilterCount > 0
                  ? 'Loosen or clear a filter to widen the search.'
                  : 'Bring records in from the Ingest page, then search them here.'
              }
              actions={
                search.activeFilterCount > 0 ? (
                  <Button variant="primary" onClick={search.resetFilters}>
                    Clear all filters
                  </Button>
                ) : null
              }
            />
          ) : (
            <>
              <JudgmentTable
                judgments={judgments}
                sortBy={search.sortBy}
                sortDir={search.sortDir}
                onSort={search.setSort}
                onSelect={onSelect}
                selectedId={selectedId}
                loading={results.isLoading}
                pageSize={search.pageSize}
              />

              {meta ? (
                <Pagination
                  meta={meta}
                  onPageChange={search.setPage}
                  onPageSizeChange={search.setPageSize}
                />
              ) : null}
            </>
          )}
        </Card>
      </div>

      <JudgmentDetailDrawer
        judgmentId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </>
  )
}
